import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  viewChild,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatDividerModule } from "@angular/material/divider";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { IOpenAiModel } from "../../interfaces/i-open-ai-model";
import { AiChatService } from "./services/ai-chat.service";
import { ChatSessionService } from "./services/chat-session.service";
import { ChatMessageService } from "./services/chat-message.service";
import { RoutePath } from "../../enums/api.enum";
import { MessageViewComponent } from "./message-view/message-view.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ApiService } from "../../services/api.service";
import { SpinnerService } from "../../shared/spinner/spinner.service";
import { INavMenuItem } from "../../interfaces/i-nav-menu-item";
import { UiLayoutService } from "../../ui-layout/services/ui-layout.service";
import { ActionType } from "../../types/app.types";
import { DialogConfirmComponent } from "../../shared/dialog-confirm/dialog-confirm.component";
import { SessionManagerComponent } from "./session-manager/session-manager.component";
import { ChatDocumentService } from "./services/chat-document.service";
import { IconsModule } from "@progress/kendo-angular-icons";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { MatChipsModule } from "@angular/material/chips";
import { InitializationService } from "../../services/initialization.service";
import { ISensitivityLabel, ISensitivityLabelExpanded } from "../../interfaces/i-sensitivity-label";
import { toISensitivityLabel, mapLegacySensitivityLabel } from "../../utilities/sensitivity-label.utils";
import { SensitivityLabelService } from "../../services/sensitivity-label.service";
import { AgentService } from "../agent/services/agent.service";
import {
  IChatSession,
  IFullDetailChatSession,
} from "../../interfaces/i-chat-session";
import { AlertEnum } from "../../shared/alert/alert.enum";
import { AlertService } from "../../shared/alert/alert.service";
import { SessionDetailsComponent } from "./session-details/session-details.component";
import { PromptLibraryComponent } from "./prompt-library/prompt-library.component";
import { ExportChatSessionService } from "../../services/export-chat-session.service";
import { AutomationUtilitiesService } from "./services/automation-utilities.service";
import { AutomationStateService } from "./services/automation-state.service";
import { AutomationExecutionService } from "./services/automation-execution.service";
import { InstructionType } from "../../enums/automation.enum";
import { QuickEditSessionComponent } from "./quick-edit-session/quick-edit-session.component";
import { AiChatPromptComponent } from "../../shared/ai-chat-prompt/ai-chat-prompt.component";
import { RightSidebarState } from "../../ui-layout/right-sidebar/right-sidebar-state";
import { SidebarCollapseMode } from "../../ui-layout/enums/sidebar-enum";
import { MultiSessionService } from "../../services/multi-session.service";

@Component({
  selector: "app-ai-chat",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    IconsModule,
    ButtonsModule,
    MessageViewComponent,
    SessionDetailsComponent,
    AiChatPromptComponent
  ],
  providers: [ExportChatSessionService],
  templateUrl: "./ai-chat.component.html",
  styleUrls: ["./ai-chat.component.scss"],
  host: {
    '(window:resize)': 'checkForTextOverflow()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatComponent implements AfterViewChecked {
  sessionLabel = viewChild<ElementRef>("sessionLabel");
  isTextOverflowing = false;
  readonly chatSessionService = inject(ChatSessionService);
  readonly chatDocumentService = inject(ChatDocumentService);
  readonly initializationService = inject(InitializationService);
  promptText = "";

  readonly dialog = inject(MatDialog);
  readonly sidebarState = inject(RightSidebarState);

  #shouldScrollToLastUserMessage = true;
  #lastUserMessageCount = 0;
  readonly chatContent = viewChild<ElementRef>('chatContent');
  readonly aiChatService = inject(AiChatService);
  readonly automationStateService = inject(AutomationStateService);
  readonly automationExecutionService = inject(AutomationExecutionService);
  readonly multiSessionService = inject(MultiSessionService);
  #automationUtilitiesService = inject(AutomationUtilitiesService);
  #agentService = inject(AgentService);
  #sensitivityLabelService = inject(SensitivityLabelService);

  /** Whether the sensitivity-label feature is enabled; label UI is hidden when disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  /** The expanded sensitivity label for the current session, resolved reactively from the backend ISensitivityLabel. */
  readonly sensitivityExpanded = computed(() => {
    const sl = this.chatSessionService.selectedSession()?.sensitivityLabels?.[0];
    return sl ? (mapLegacySensitivityLabel(sl) ?? null) : null;
  });

  /** Display string for the current session's sensitivity label. */
  readonly sensitivityLabel = computed(() => {
    const expanded = this.sensitivityExpanded();
    return expanded ? this.#agentService.getSensitivityLabelString(expanded) : '';
  });
  #alertService = inject(AlertService);
  #chatMessageService = inject(ChatMessageService);
  #route = inject(ActivatedRoute);
  #apiService = inject(ApiService);
  #spinnerService = inject(SpinnerService);
  #uiLayoutService = inject(UiLayoutService);
  #router = inject(Router);

  constructor() {
    this.#route.params.subscribe((params) => {
      // Set the selected session id
      const sessionId = params["sessionId"];

      if (sessionId) {
        this.chatSessionService.routeSessionId = sessionId;
      }

      // We set the session ID here to update the selectedSession and then set the message to the new selected session's messages.
      // We do it here to fix a bug where if a user refreshes the page on a session, say Session #2, the messages from the most recent session that isn't Session #2
      // would appear in the chat window of Session #2 instead of Session #2's messages.
      this.chatSessionService.selectedSessionId.set(sessionId);
      this.#chatMessageService.messages.set(this.chatSessionService.selectedSession()?.messages || []);

       // Reset scroll flag when switching sessions or on initial load
       // This ensures we scroll to the last user message when loading a session's existing messages
       this.#shouldScrollToLastUserMessage = true;
       // Initialize user message count to trigger scroll on first user message
       const initialMessages = this.chatSessionService.selectedSession()?.messages || [];
       this.#lastUserMessageCount = initialMessages.filter(m => m.role === 'user').length;

      // checks for if the currently selected session has any messages stuck in progress.
      // if so, we set those to a default error message.
      // this is to fix an issue with the chat not updating the message status when the user navigates away from the chat
      // and the Cancel button appearing in the chat bubble. when click, will remove the wrong message.
      this.aiChatService.checkForStuckInProgressMessages();

      // Mark session as "read" by resetting Success/Error status to Default
      this.multiSessionService.markSessionAsRead(sessionId);

      // Handle opening dialogs for paused sessions when navigating to them
      // This handles the case where user navigates away from a paused session and comes back
      if (sessionId && !this.#spinnerService.isLoading()) {
        // Use setTimeout to ensure session state is loaded after route navigation
        setTimeout(() => {
          const sessionState = this.automationStateService.getSessionState(sessionId);

          // Only open if not already in the queue (queue will handle workflow pauses)
          const queueHasSession = this.automationExecutionService.userInputNeeded()
            .some(item => item.sessionId === sessionId);

          if (!queueHasSession) {
            // Determine if this is a workflow by checking for workflow execution state
            // Note: isWorkflowInProgress can be false even for workflows when paused
            const isWorkflow = !!(sessionState?.workflowExecutionState || sessionState?.workflowBeingExecuted);

            if (sessionState?.isWaitingForUserInput) {
              this.#automationUtilitiesService.openInstructionUserInputDialog(isWorkflow, sessionId);
            }

            if (sessionState?.isWaitingForFileUpload) {
              // For standalone instructions, auto-send after upload; for workflows, don't auto-send
              const shouldSendMessage = !isWorkflow;
              this.#automationUtilitiesService.openInstructionUserFileUploadDialog(isWorkflow, shouldSendMessage, sessionId);
            }
          }
        }, 100);
      }
    });

    // Single consolidated effect to handle all reactive state changes
    effect(() => {
      // Handle session label changes and text overflow
      const session = this.chatSessionService.selectedSession();
      const selectedSessionId = this.chatSessionService.selectedSessionId();

      if (session?.label) {
        // Wait for DOM to update with new label
        setTimeout(() => this.checkForTextOverflow(), 0);
      }

      const sensitivityLabels = session?.sensitivityLabels;
      if (session && (!sensitivityLabels || sensitivityLabels.length === 0)) {
        // No sensitivity label — prompt the user to set one
        if (selectedSessionId) {
          this.openCreateOrEditSessionDialog("update");
        } else {
          this.openCreateOrEditSessionDialog("create");
        }
      }

      // Handle Automation Library selections
      // Note: Prompt text is now directly set on the session's input data
      // and will be automatically reflected in the form

      // Apply model settings if available
      const config = this.automationStateService.instructionInProgress()!;
      if (
        config?.details?.model ||
        config?.details?.temperature ||
        config?.details?.topP
      ) {
        // Configuration is handled by updateSessionConfig method when sending the message
      }
    });

    // Effect to handle user input dialog opening based on signal from AutomationExecutionService
    // Opens dialogs when automation pauses mid-execution (only if viewing that session)
    effect(() => {
      const userInputQueue = this.automationExecutionService.userInputNeeded();

      if (userInputQueue.length > 0 && !this.#spinnerService.isLoading()) {
        const currentSessionId = this.chatSessionService.selectedSessionId();

        // Process all pending requests
        userInputQueue.forEach(({ sessionId, inputType }) => {
          // Only open dialog if we're viewing the session that needs input
          if (sessionId === currentSessionId) {
            if (inputType === InstructionType.CollectInformation) {
              this.#automationUtilitiesService.openInstructionUserInputDialog(true, sessionId);
            } else if (inputType === InstructionType.FileUpload) {
              this.#automationUtilitiesService.openInstructionUserFileUploadDialog(true, false, sessionId);
            }
          }
          // If not viewing the paused session, it will open via route params subscription
          // when navigating to that session
        });

        // Clear the queue after handling all requests
        this.automationExecutionService.userInputNeeded.set([]);
      }
    });

    // Effect to watch for new user messages and trigger scrolling when one is added
    effect(() => {
      const messages = this.#chatMessageService.messages();
      const userMessages = messages?.filter(m => m.role === 'user') || [];
      const currentUserMessageCount = userMessages.length;

      // If we have more user messages than before, scroll to the last one
      if (currentUserMessageCount > this.#lastUserMessageCount) {
        this.#shouldScrollToLastUserMessage = true;
      }

      // Update the tracked user message count
      this.#lastUserMessageCount = currentUserMessageCount;
    });
  }

   ngAfterViewChecked(): void {
     // Scroll to position the last user message at the top when flag is set
     if (this.#shouldScrollToLastUserMessage) {
       // Clear immediately to avoid scheduling multiple timers across change-detection cycles
       this.#shouldScrollToLastUserMessage = false;
       // Use requestAnimationFrame to ensure DOM is fully rendered
       requestAnimationFrame(() => {
         // Add delay to ensure all rendering and layout shifts are complete
         // Increased to 50ms to accommodate streaming message rendering
         setTimeout(() => this.#scrollToLastUserMessage(), 50);
       });
     }
   }

  /**
   * Called when a prompt is submitted from the AiChatPromptComponent.
   * Triggers scrolling to the last user message.
   */
  onPromptSubmitted(): void {
    this.#shouldScrollToLastUserMessage = true;
  }

  /**
   * Checks if the session label text is overflowing its container
   */
  checkForTextOverflow(): void {
    const sessionLabelEl = this.sessionLabel?.();
    if (sessionLabelEl) {
      const element = sessionLabelEl.nativeElement;
      this.isTextOverflowing = element.scrollWidth > element.clientWidth;
    }
  }


  toggleSidebar(): void {
    if (!this.sidebarState.currentComponent()) {
      this.sidebarState.open(PromptLibraryComponent, {
        collapseMode: SidebarCollapseMode.Hidden,
        showToggleButton: false,
        sidebarWidth: 400
      });
    } else {
      this.sidebarState.toggle();
    }
  }


  /**
   * Opens a confirmation dialog to delete the current chat session.
   *
   * This method displays a dialog asking the user to confirm deletion of the selected chat session.
   * If the user confirms, it calls the private method `#deleteSession` to perform the deletion.
   */
  openDeleteSessionDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: `Are you sure you want to delete your session "${
        this.chatSessionService.selectedSession()?.label
      }"?`,
      buttonLabel: "Delete",
      title: "Confirm",
      tertiaryButtonLabel: "",
      showTertiaryButton: false,
      showSecondaryButton: true,
    };
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.#deleteSession();
      }
    });
  }

  // TODO: This functionality is also in the UiLayoutComponent.  We should make this into a service to centralize it.
  /**
   * Opens a dialog for creating or editing a chat session.
   *
   * This method configures and displays the session manager dialog, passing the specified action
   * (either 'create' or 'edit'). After the dialog closes, it processes the response to update
   * the session's sensitivity label, agents, and other details as needed. If the sensitivity label
   * changes, a system prompt is added and the session is persisted. Alerts are shown to indicate
   * the result of the operation.
   *
   * @param action The action to perform: 'create' or 'edit'.
   */
  openCreateOrEditSessionDialog(action: ActionType) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = "100vw";
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = "100vw";
    dialogConfig.maxHeight = '100vh';
    dialogConfig.data = { action: action };

    const dialogRef = this.dialog.open(SessionManagerComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .subscribe(
        async (response: {
          id: string;
          action: ActionType;
          sensitivityLabel: ISensitivityLabelExpanded;
          temperature: number;
          topP: number;
          model: IOpenAiModel;
          isLabelChanged: boolean;
          sessionLabel: string;
          agents: string[];
          sessionData: IFullDetailChatSession;
        }) => {
          if (response.id) {
            if (response.action === "edit") {
              const currentSession = this.chatSessionService.selectedSession();


              if (currentSession) {
                const updatedSession: IChatSession = {
                  ...currentSession,
                  agents: response.agents,
                  sensitivityLabels: [toISensitivityLabel(response.sensitivityLabel)],
                  model: response.model,
                  temperature: response.temperature,
                  topP: response.topP,
                };
                this.chatSessionService.selectedSessionId.set(
                  updatedSession.id
                ); // Ensure the session ID is set correctly
                const chatSessions = this.chatSessionService.chatSessions();
                if (chatSessions) {
                  this.chatSessionService.chatSessions.set(
                    chatSessions.map((session) =>
                      session.id === updatedSession.id
                        ? updatedSession
                        : session
                    )
                  );
                }
              }

              // Track changes for sensitivity label, agents, model, temperature, and topP
              const updatedFields = [];

              // Check if sensitivity label was changed (compare by canonical code)
              if (
                currentSession &&
                currentSession.sensitivityLabels &&
                response.sensitivityLabel.id !== (currentSession.sensitivityLabels[0]?.code ?? '')
              ) {
                updatedFields.push(
                  `- Sensitivity Label: from ${this.#agentService.getSensitivityLabelString(
                    currentSession.sensitivityLabels[0]
                  )} to ${this.#agentService.getSensitivityLabelString(
                    response.sensitivityLabel
                  )}`
                );

                // Handle sensitivity label mismatch
                this.aiChatService.handleSensitivityLabelMismatch(currentSession.id, response.sensitivityLabel.id);

                // Remove any attached documents that exceed the new (lower) sensitivity label
                const currentDocs = this.chatSessionService.getSessionInputData(this.chatSessionService.selectedSessionId()).documents;
                this.chatSessionService.updateSessionInputDataDocuments(currentDocs, true);
              }

              // Check if agents were changed and add to updatedFields if they were
              if (currentSession && currentSession.agents) {
                const agentChanges = await this.aiChatService.formatAgentChanges(
                  currentSession.agents,
                  response.agents
                );
                if (agentChanges) {
                  updatedFields.push(agentChanges);
                }
              }

              // Check for model, temperature, and topP changes
              const aiConfigChanges = this.aiChatService.handleAiConfigMismatch(
                currentSession,
                response.model,  // Use the model from the dialog response
                response.temperature,  // Use the temperature from the dialog response
                response.topP  // Use the topP from the dialog response
              );

              // Add any AI config changes to the updatedFields array
              updatedFields.push(...aiConfigChanges);

              // Create system message for configuration changes if needed
              const changesMade = this.aiChatService.createConfigChangeSystemMessage(currentSession, updatedFields, response.id);

              // If no changes were made or no system message was created, show a simple success alert
              if (!changesMade) {
                this.#alertService.showAlert({
                  message: `Session ${response.sessionLabel} has been saved.`,
                  type: AlertEnum.success,
                });
              }
            } else {
              this.#alertService.showAlert({
                message: `Session ${response.sessionLabel} has been saved.`,
                type: AlertEnum.success,
              });
              // NOTE: For some reason, the dialog is not closing when the action is 'create' and there are no other sessions.
              // This is a workaround to close the dialog manually.
              this.dialog.closeAll();
            }

            this.chatSessionService.completeCreateOrEditSession(
              response.id,
              response.action
            );
          }
        }
      );
  }

  /**
   * Opens a confirmation dialog to clear the current chat session.
   *
   * This method displays a dialog asking the user to confirm clearing all messages and attachments
   * from the current chat session. If the user confirms, it calls the private method `#clearSession`
   * to perform the clearing operation.
   */
  openClearSessionDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: `Are you sure you want to clear your chat?`,
      buttonLabel: "Clear",
      title: "Confirm",
      tertiaryButtonLabel: "",
      showTertiaryButton: false,
      showSecondaryButton: true,
    };
    dialogConfig.disableClose = true;

    const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.#clearSession();
      }
    });
  }

  /**
   * Opens a dialog for quick editing of agent selections and sensitivity label for the current session.
   *
   * This method displays a compact dialog that allows the user to quickly modify the agent selections
   * and sensitivity label of the current chat session without having to navigate away from the chat.
   * When changes are made, it adds a system message to inform the user about what was updated.
   */
  openQuickEditSessionDialog() {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = false;
      dialogConfig.width = "70vw";
      dialogConfig.maxWidth = "90vw";

      // Get the current session before opening the dialog to track changes
      const currentSession = this.chatSessionService.selectedSession();
      const previousAgents = currentSession?.agents ? [...currentSession.agents] : [];

      const dialogRef = this.dialog.open(QuickEditSessionComponent, dialogConfig);

      dialogRef
        .afterClosed()
        .subscribe(
          async (result: {
              result: boolean,
              agents: string[],
              sensitivityLabel: ISensitivityLabelExpanded,
              isLabelChanged: boolean
            }) => {
            if (result && result.result) {
              // Session was updated
              const sessionId = this.chatSessionService.selectedSessionId();
              if (sessionId) {

                const updatedSession: IChatSession = {
                  ...currentSession!,
                  agents: result.agents,
                  sensitivityLabels: [toISensitivityLabel(result.sensitivityLabel)],
                };
                this.chatSessionService.selectedSessionId.set(
                  updatedSession.id
                ); // Ensure the session ID is set correctly
                const chatSessions = this.chatSessionService.chatSessions();
                if (chatSessions) {
                  this.chatSessionService.chatSessions.set(
                    chatSessions.map((session) =>
                      session.id === updatedSession.id
                        ? updatedSession
                        : session
                    )
                  );
                }

                // Track changes for agents and sensitivity labels
                const updatedFields = [];

                // Check if sensitivity label was changed (compare by canonical code)
                if (
                  currentSession?.sensitivityLabels?.[0] &&
                  result.sensitivityLabel &&
                  result.sensitivityLabel.id !== (currentSession.sensitivityLabels[0].code ?? '')
                ) {
                  updatedFields.push(
                    `- Sensitivity Label: from ${this.#agentService.getSensitivityLabelString(
                      currentSession.sensitivityLabels[0]
                    )} to ${this.#agentService.getSensitivityLabelString(
                      result.sensitivityLabel
                    )}`
                  );

                  // Handle sensitivity label mismatch
                  this.aiChatService.handleSensitivityLabelMismatch(sessionId, result.sensitivityLabel.id);

                  // Remove any attached documents that exceed the new (lower) sensitivity label
                  const currentDocs = this.chatSessionService.getSessionInputData(this.chatSessionService.selectedSessionId()).documents;
                  this.chatSessionService.updateSessionInputDataDocuments(currentDocs, true);
                }

                // Check if agents were changed and add to updatedFields if they were
                const agentChanges = await this.aiChatService.formatAgentChanges(previousAgents, result.agents);
                if (agentChanges) {
                  updatedFields.push(agentChanges);
                }

                // Create system message for configuration changes if needed
                const changesMade = this.aiChatService.createConfigChangeSystemMessage(currentSession, updatedFields, sessionId);

                // If no changes were made or no system message was created, show a simple success alert
                if (!changesMade) {
                  this.#alertService.showAlert({
                    message: `Session ${currentSession?.label} has been saved.`,
                    type: AlertEnum.success,
                  });
                }

                this.chatSessionService.completeCreateOrEditSession(
                  sessionId + "",
                  "edit"
                );
              }
            }
          }
        );
    }

  // TODO add functionality to do file upload
  // openInstructionUserInputDialog(isWf = true) {
  //   const dialogConfig = new MatDialogConfig();
  //   dialogConfig.disableClose = false;
  //   dialogConfig.width = '55vw';
  //   dialogConfig.maxWidth = '55vw';
  //
  //   const dialogRef = this.dialog.open(InstructionUserInputComponent, dialogConfig);
  //
  //   dialogRef.afterClosed().subscribe((response: string) => {
  //     this.automationUtilitiesService.isWaitingForUserInput.set(false);
  //     if (response) {
  //      console.log('openInstructionUserInputDialog', response);
  //      if (isWf) {
  //        this.#automationExecutionService.continueInstructionExecutionWithUserInput(response);
  //      }
  //     }
  //   });
  // }

  // openExportDialog() {
  //   const dialogConfig = new MatDialogConfig();
  //   dialogConfig.disableClose = false;
  //   dialogConfig.width = '35vw';
  //   // dialogConfig.height = '39vh';
  //   dialogConfig.maxWidth = '35vw';
  //   dialogConfig.maxHeight = '39vh';
  //   const dialogRef = this.dialog.open(ExportChatDialogComponent, dialogConfig);
  //
  //   dialogRef.afterClosed().subscribe(fileName => {
  //     if (fileName) {
  //       this.#exportChat(fileName);
  //       // this.#exportToDocxJS(fileName);
  //     }
  //   });
  // }

  /**
   * Determines if the "No session exists" message should be shown.
   * Checks if the route session ID exists in the user's chat sessions.
   */
  showNoSessionExistMessage(): boolean {
    const chatSessions = this.chatSessionService.chatSessions();

    if (
      this.chatSessionService.routeSessionId &&
      chatSessions &&
      chatSessions.length > 0
    ) {
      return !chatSessions.some(
        (session) => session.id === this.chatSessionService.routeSessionId
      );
    }

    return false;
  }

  /**
   * Clears all messages and attachments from the currently selected chat session.
   *
   * This method resets the messages and selected documents for the active session,
   * updates the chat message state, and persists the cleared session to the backend.
   * If an error occurs during persistence, it is logged to the console.
   *
   * @private
   */
  #clearSession() {
    // Clear the messages in the selected session
    const selectedSession = this.chatSessionService.selectedSession();
    if (selectedSession) {
      selectedSession.messages = [];
      this.chatDocumentService.selectedDocuments.set([]);
      this.#chatMessageService.messages.set([]);
    }

    // Now save the updated session with no messages
    this.#apiService.persistSession(selectedSession!).subscribe({
      next: (response) => {},
      error: (error) => {
        console.error("Error persisting session", error);
      },
    });
  }

  /**
   * Deletes the currently selected chat session and updates the UI.
   *
   * This method removes the selected session from the backend and updates the navigation menu and session list.
   * If there are remaining sessions, it selects the first one and updates the UI accordingly.
   * If no sessions remain, it clears the messages, resets the selected session, navigates to the session page,
   * and opens the create session dialog.
   *
   * Shows a spinner during the operation and handles errors by logging them to the console.
   *
   * @private
   */
  #deleteSession() {
    this.#spinnerService.show();
    // TODO: Reset InstructionInProgress or WorkflowBeingExecuted flag if needed

    // Delete the saved text and attached docs from the chat session service
    const sessionId = this.chatSessionService.selectedSessionId();
    this.chatSessionService.deleteSessionInputData(sessionId);

    this.#apiService
      .deleteSession(this.chatSessionService.selectedSessionId())
      .subscribe({
        next: (response) => {
          this.#apiService.getSessions().subscribe({
            next: (resp) => {
              // First remove the deleted session from the nav menu
              const items = this.#uiLayoutService
                .navItems()
                .filter(
                  (item) =>
                    item.id !== this.chatSessionService.selectedSessionId()
                );
              this.#uiLayoutService.navItems.set(items);
              // Set the updated chat sessions.
              this.chatSessionService.chatSessions.set(resp);
              // Check if there are any sessions left, if so, set the first one as the selected session and update the URL.
              // If not, clear the messages and the selected session. Then navigate to the Session Page.
              if (resp.length > 0) {
                // Set the current session.  Use the first in the list.
                this.chatSessionService.selectedSessionId.set(resp[0].id);
                this.#chatMessageService.messages.set(resp[0].messages);
                // Get the latest selected session.  We use this to make it selected in the nav menu
                const newSelectedItem: INavMenuItem | undefined =
                  this.#uiLayoutService
                    .navItems()
                    .find(
                      (item) =>
                        item.id === this.chatSessionService.selectedSessionId()
                    );
                if (newSelectedItem) {
                  // If there is a selected session, make it selected in the nav menu.
                  const updatedNavItems = this.#uiLayoutService
                    .navItems()
                    .map((navItem) => {
                      return {
                        ...navItem,
                        selected: navItem === newSelectedItem,
                      };
                    });
                  this.#uiLayoutService.navItems.set(updatedNavItems);
                  // Update the URL to reflect the selected session
                  this.#router.navigateByUrl(
                    `${
                      RoutePath.Session
                    }/${this.chatSessionService.selectedSessionId()}`
                  );
                } else {
                  this.#router.navigateByUrl(RoutePath.Session);
                }
              } else {
                // If there are no sessions left, clear the messages and the selected session.
                this.#chatMessageService.messages.set([]);
                this.chatSessionService.selectedSessionId.set("");
                // Navigate to the Session Page
                this.#router.navigateByUrl(RoutePath.Session);
                // Open the create session dialog
                this.openCreateOrEditSessionDialog("create");
              }

              this.#spinnerService.hide();
            },
          });
        },
      });
  }

   /**
    * Scrolls the chat content to the last conversation container.
    * With the new conversation-container structure, each exchange (user message + AI response)
    * occupies a full viewport height. This scroll ensures the latest conversation is visible.
    *
    * This creates a better experience where:
    * - User message appears at top of viewport
    * - AI response streams in below with plenty of space
    * - Scrolling reveals previous conversations
    */
   #scrollToLastUserMessage(): void {
      const chatContentElement = this.chatContent()?.nativeElement;
      if (!chatContentElement) return;

      // Find all conversation containers
      const conversationContainers = chatContentElement.querySelectorAll('.conversation-container');
      if (conversationContainers.length === 0) return;

      // Scroll to the last conversation container
      const lastContainer = conversationContainers[conversationContainers.length - 1] as HTMLElement;
      const containerTop = lastContainer.offsetTop;

      // Add offset to ensure the entire last user-query element is visible (not cut off at top)
      const topOffset = 50; // pixels
      // Smooth scroll to the conversation
      chatContentElement.scrollTo({ top: Math.max(0, containerTop - topOffset), behavior: 'smooth' });
    }
}
